import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

export type FinancialsRedeemableDailyFilters = {
  start_date: string;
  end_date: string;
};

export type FinancialsRedeemablePoint = {
  date: string;
  value: number;
};

type RawRedeemableRow = {
  date?: string | null;
  redeemable_cash?: number | string | null;
};

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const buildDateRange = (start: string, end: string): string[] => {
  if (!start || !end) return [];
  const startDay = dayjs(start, ISO_DATE_FORMAT, true);
  const endDay = dayjs(end, ISO_DATE_FORMAT, true);
  if (!startDay.isValid() || !endDay.isValid()) return [];
  if (endDay.isBefore(startDay, "day")) return [];

  const dates: string[] = [];
  let cursor = startDay;
  while (!cursor.isAfter(endDay, "day")) {
    dates.push(cursor.format(ISO_DATE_FORMAT));
    cursor = cursor.add(1, "day");
  }
  return dates;
};

/** Maps API rows to chart points and fills missing days with zeros. */
export const parseFinancialsRedeemableDailyResponse = (
  body: unknown,
  filters: FinancialsRedeemableDailyFilters,
): FinancialsRedeemablePoint[] => {
  const rawList = Array.isArray((body as { data?: unknown })?.data)
    ? ((body as { data: RawRedeemableRow[] }).data ?? [])
    : [];

  const byDate = new Map<string, FinancialsRedeemablePoint>();
  for (const row of rawList) {
    const date = typeof row?.date === "string" ? row.date : "";
    if (!date) continue;
    byDate.set(date, {
      date,
      value: coerceNumber(row.redeemable_cash),
    });
  }

  const denseDates = buildDateRange(filters.start_date, filters.end_date);
  if (denseDates.length > 0) {
    return denseDates.map(
      (date) => byDate.get(date) ?? { date, value: 0 },
    );
  }

  return Array.from(byDate.values());
};

export const fetchFinancialsRedeemableDaily = async (
  filters: FinancialsRedeemableDailyFilters,
): Promise<FinancialsRedeemablePoint[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.FINANCIALS_REDEEMABLE_DAILY, {
    params: filters,
  });
  return parseFinancialsRedeemableDailyResponse(response?.data, filters);
};

export const useGetFinancialsRedeemableDaily = (
  filters: FinancialsRedeemableDailyFilters,
) => {
  const { start_date, end_date } = filters;

  return useQuery<FinancialsRedeemablePoint[], Error>({
    queryKey: ["FINANCIALS_REDEEMABLE_DAILY", start_date, end_date],
    queryFn: () => fetchFinancialsRedeemableDaily(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
